// src/browserLogs.ts
var toHrTime = (millis) => [
  Math.trunc(millis / 1e3),
  Math.round(millis % 1e3 * 1e6)
];
var isScalar = (value) => value == null || ["string", "number", "boolean"].includes(typeof value);
var BrowserLogRecord = class {
  constructor(resource, instrumentationScope, limits, record) {
    this.resource = resource;
    this.instrumentationScope = instrumentationScope;
    this.limits = limits;
    const now = Date.now();
    this.hrTime = toHrTime(Number(record.timestamp ?? now));
    this.hrTimeObserved = toHrTime(Number(record.observedTimestamp ?? now));
    this.severityNumber = record.severityNumber;
    this.severityText = record.severityText;
    this.body = record.body;
    this.eventName = record.eventName;
    for (const [key, value] of Object.entries(record.attributes ?? {}))
      this.setAttribute(key, value);
  }
  hrTime;
  hrTimeObserved;
  attributes = {};
  severityNumber;
  severityText;
  body;
  eventName;
  droppedAttributesCount = 0;
  attributeCount = 0;
  readonly = false;
  /** The SDK's limits: bounded attribute count, truncated strings, frozen after emit. */
  setAttribute(key, value) {
    if (this.readonly || !key || !isScalar(value)) return this;
    const isNew = !Object.hasOwn(this.attributes, key);
    if (isNew && this.attributeCount >= this.limits.attributeCountLimit) {
      this.droppedAttributesCount++;
      return this;
    }
    this.attributes[key] = typeof value === "string" ? value.slice(0, this.limits.attributeValueLengthLimit) : value;
    if (isNew) this.attributeCount++;
    return this;
  }
  freeze() {
    this.readonly = true;
  }
};
var BrowserLoggerProvider = class {
  constructor(options) {
    this.options = options;
  }
  stopped = false;
  getLogger(name, version) {
    return {
      emit: (input) => {
        if (this.stopped) return;
        const { resource, limits, processors } = this.options;
        const record = new BrowserLogRecord(resource, { name, version }, limits, input);
        for (const processor of processors) processor.onEmit(record);
        record.freeze();
      }
    };
  }
  async shutdown() {
    this.stopped = true;
    await Promise.all(this.options.processors.map((processor) => processor.shutdown()));
  }
};

// src/browserOtelApi.ts
var BrowserContext = class _BrowserContext {
  constructor(values = /* @__PURE__ */ new Map()) {
    this.values = values;
  }
  getValue(key) {
    return this.values.get(key);
  }
  setValue(key, value) {
    return new _BrowserContext(new Map(this.values).set(key, value));
  }
  deleteValue(key) {
    const values = new Map(this.values);
    values.delete(key);
    return new _BrowserContext(values);
  }
};
var ROOT_CONTEXT = new BrowserContext();
var createContextKey = (description) => Symbol.for(description);
var context = {
  active: () => ROOT_CONTEXT,
  with: (_context, fn, thisArg, ...args) => fn.call(thisArg, ...args)
};
var trace = { getSpan: (_context) => void 0 };
var SpanStatusCode = { UNSET: 0, OK: 1, ERROR: 2 };
var logs = { getLogger: () => ({ emit: () => {
} }) };

// src/contract.ts
var SCHEMA_VERSION = "1.0.0";
var POLICY_VERSION = "1.0.0";
var LEVELS = { trace: 1, debug: 5, info: 9, warn: 13, error: 17, fatal: 21 };
function defineEvent(definition) {
  if (!safeEventName(definition.name) || !definition.message || definition.message.length > 180 || !definition.owner || definition.classification !== "operational")
    throw new Error("Invalid static logging definition");
  for (const key of Object.keys(definition.attributes))
    if (!/^(di\.[a-z][a-z0-9_]*|http\.(request.method|route|response.status_code)|db.system.name|gen_ai.system|gen_ai.request.model)$/.test(
      key
    ) || TRUSTED_KEYS.has(key) || /prompt|question|completion|history|email|cookie|token(?!s)|password|secret|payload|body|headers|visitor|session|url(?!_)|authorization/i.test(
      key
    ))
      throw new Error("Forbidden logging attribute");
  return Object.freeze(definition);
}
var TRUSTED_KEYS = /* @__PURE__ */ new Set([
  "di.request_id",
  "di.parent_request_id",
  "di.render_request_id",
  "di.chat_turn_id",
  "di.conversation_id",
  "di.chatbot_id",
  "di.organization_id",
  "di.job_run_id",
  "di.event_id",
  "di.log_schema_version",
  "di.policy_version"
]);
function applyOutcome(operation, outcome, errorId) {
  for (let state = operation; state; state = state.parent) {
    if (state.outcome !== "failure" && !(state.outcome === "degraded" && outcome === "success"))
      state.outcome = outcome;
    if (errorId && (!state.errorId || outcome === "failure" && state.errorOutcome !== "failure")) {
      state.errorId = errorId;
      state.errorOutcome = outcome;
    }
  }
}
var OPERATION_KEY = createContextKey("di.observability.operation.v1");
function currentOperation() {
  return context.active().getValue(OPERATION_KEY);
}
var safeIdentifier = (value) => typeof value === "string" && /^[a-zA-Z0-9_-]{1,128}$/.test(value) ? value : void 0;
var safeCategory = (value) => typeof value === "string" && /^[a-zA-Z0-9_.:-]{1,96}$/.test(value) ? value : void 0;
var safeEventName = (value) => typeof value === "string" && /^[a-z][a-z0-9_.]{2,140}$/.test(value) ? value : void 0;
function own(value, key) {
  try {
    if (!value || typeof value !== "object" && typeof value !== "function") return void 0;
    const d = Object.getOwnPropertyDescriptor(value, key);
    return d && "value" in d ? d.value : void 0;
  } catch {
    return void 0;
  }
}
function sanitizeAttributes(fields, input) {
  const result = {};
  let invalid = false;
  try {
    if (input && Object.keys(input).some((key) => !Object.hasOwn(fields, key))) invalid = true;
  } catch {
    invalid = true;
  }
  for (const [key, field] of Object.entries(fields)) {
    const value = own(input, key);
    if (value === void 0 && field.optional) continue;
    let safe;
    if (field.type === "number")
      safe = typeof value === "number" && Number.isFinite(value) && Math.abs(value) < 1e15 ? value : void 0;
    else if (field.type === "boolean") safe = typeof value === "boolean" ? value : void 0;
    else if (field.type === "identifier") safe = safeIdentifier(value);
    else if (field.type === "event") safe = safeEventName(value);
    else if (field.type === "route")
      safe = typeof value === "string" && /^[a-zA-Z0-9_/:.* -]{1,180}$/.test(value) && !value.includes("://") ? value : void 0;
    else safe = safeCategory(value);
    if (safe === void 0 || field.values && !field.values.includes(String(safe)))
      invalid = true;
    else result[key] = safe;
  }
  return { attributes: result, invalid };
}
var nativeStackGetter = Object.getOwnPropertyDescriptor(new Error(), "stack")?.get;
function errorStack(error) {
  const direct = own(error, "stack");
  if (direct !== void 0) return direct;
  try {
    const descriptor = error && typeof error === "object" ? Object.getOwnPropertyDescriptor(error, "stack") : void 0;
    return nativeStackGetter && descriptor?.get === nativeStackGetter ? nativeStackGetter.call(error) : void 0;
  } catch {
    return void 0;
  }
}
var ERROR_TYPES = /* @__PURE__ */ new Set([
  "Error",
  "TypeError",
  "ReferenceError",
  "RangeError",
  "SyntaxError",
  "URIError",
  "EvalError",
  "AggregateError",
  "AbortError",
  // Axios names a cancelled request CanceledError, its counterpart of fetch's AbortError.
  "CanceledError",
  "TimeoutError",
  // A thrown value that is not an Error. Browser reports carry it as their exception type.
  "UnknownThrownValue",
  "APIError",
  "APIConnectionError",
  "APIConnectionTimeoutError",
  "RateLimitError",
  "AuthenticationError",
  "PermissionDeniedError",
  "BadRequestError",
  "InternalServerError",
  "NotFoundError",
  "UnprocessableEntityError",
  "DatabaseError",
  "FetchError",
  // The widget's error for a response with an error status; its status is reported beside it.
  "HttpStatusError",
  "AxiosError",
  "VertexAIError",
  "ClientError",
  "ValidationError",
  // Provider SDK error names: without them every provider failure collapses into one group.
  "ApiError",
  "AI_APICallError",
  "AI_RetryError",
  "FirstTokenTimeoutError",
  "StreamStalledError",
  "AnswerTooLongError",
  "ThrottlingException",
  "ServiceUnavailableException",
  "ModelTimeoutException",
  "ModelNotReadyException",
  "ValidationException",
  "AccessDeniedException"
]);
var nativeDomExceptionName = typeof DOMException === "function" ? Object.getOwnPropertyDescriptor(DOMException.prototype, "name")?.get : void 0;
function exceptionType(error) {
  try {
    if (nativeDomExceptionName && typeof DOMException === "function" && error instanceof DOMException) {
      const name = nativeDomExceptionName.call(error);
      return typeof name === "string" && ERROR_TYPES.has(name) ? name : "Error";
    }
    let value = error;
    for (let depth = 0; value && typeof value === "object" && depth < 5; depth++) {
      const name = own(value, "name");
      if (name === "error" && typeof own(value, "severity") === "string") return "DatabaseError";
      if (typeof name === "string") return ERROR_TYPES.has(name) ? name : "ApplicationError";
      value = Object.getPrototypeOf(value);
    }
    return error instanceof Error ? "Error" : "UnknownThrownValue";
  } catch {
    return "UnknownThrownValue";
  }
}
var SAFE_EXCEPTION_CODE = /^(?:[0-9A-Z]{5}|E[A-Z_]{2,30}|(?:UND_)?ERR_[A-Z_]{2,50}|AADSTS[0-9]{5,8}|[a-z][a-z0-9_]{2,63})$/;
function exceptionCode(error) {
  const nested = own(error, "error");
  for (const value of [
    own(error, "code"),
    own(nested, "code"),
    own(error, "type"),
    own(nested, "type")
  ]) {
    const code = safeCategory(value);
    if (code && code !== "error" && SAFE_EXCEPTION_CODE.test(code)) return code;
  }
  return void 0;
}
function exceptionStatus(error) {
  for (const value of [
    own(error, "status"),
    own(error, "statusCode"),
    own(own(error, "$metadata"), "httpStatusCode"),
    own(own(error, "response"), "status")
  ])
    if (typeof value === "number" && Number.isInteger(value) && value >= 100 && value <= 599)
      return value;
  return void 0;
}
function safeException(error) {
  const type = exceptionType(error);
  const stack = errorStack(error);
  const frames = typeof stack === "string" ? stack.split("\n").slice(1, 65).map((line) => {
    const match = /(?:\/|\\|\(|\s)([A-Za-z0-9_.-]+\.(?:[cm]?[jt]sx?)):(\d{1,7}):(\d{1,7})\)?\s*$/.exec(
      line
    );
    return match ? `at ${match[1]}:${match[2]}:${match[3]}` : void 0;
  }).filter(Boolean).join("\n").slice(0, 8192) : "";
  const attrs = {
    "exception.type": type,
    "exception.message": "Operation failed; upstream message omitted",
    "di.stack_available": !!frames
  };
  if (frames) attrs["exception.stacktrace"] = `${type}
${frames}`;
  let code = exceptionCode(error);
  let status = exceptionStatus(error);
  const causes = [];
  const seen = /* @__PURE__ */ new Set([error]);
  const pending = [];
  const enqueue = (value) => {
    pending.push(own(value, "cause"));
    const nested = own(value, "errors");
    if (Array.isArray(nested)) pending.push(...nested.slice(0, 3));
  };
  enqueue(error);
  while (causes.length < 3 && pending.length) {
    const cause = pending.shift();
    if (!cause || seen.has(cause)) continue;
    seen.add(cause);
    causes.push(exceptionType(cause));
    code ??= exceptionCode(cause);
    status ??= exceptionStatus(cause);
    enqueue(cause);
  }
  if (code) attrs["di.exception_code"] = code;
  if (status !== void 0) attrs["di.exception_status"] = status;
  if (causes.length) attrs["di.exception_causes"] = causes.join(" <- ");
  return attrs;
}
var BREADCRUMB_BUDGET = /* @__PURE__ */ Symbol.for("di.observability.breadcrumb-budget.v1");
var globalBudget = globalThis;
var breadcrumbBudget = globalBudget[BREADCRUMB_BUDGET] ??= {
  buffers: /* @__PURE__ */ new Set(),
  bytes: 0,
  lastSweep: 0
};
var MAX_BREADCRUMB_BYTES = 8 * 1024 * 1024;
var MAX_BREADCRUMB_BUFFERS = 1024;
var BreadcrumbBuffer = class {
  constructor(maxCount = 20, maxBytes = 8192, ttlMs = 15 * 6e4) {
    this.maxCount = maxCount;
    this.maxBytes = maxBytes;
    this.ttlMs = ttlMs;
    this.expiresAt = Date.now() + ttlMs;
  }
  entries = [];
  bytes = 0;
  expiresAt;
  add(name, fields = {}) {
    if (!safeCategory(name)) return;
    const data = Object.fromEntries(
      Object.entries(fields).slice(0, 8).filter(
        ([k, v]) => safeCategory(k) && (typeof v === "number" || typeof v === "boolean" || safeCategory(v))
      )
    );
    const line = JSON.stringify({ event: name, ...data });
    if (line.length > this.maxBytes) return;
    const now = Date.now();
    if (now > this.expiresAt) this.clear();
    if (now - breadcrumbBudget.lastSweep >= 1e3) {
      breadcrumbBudget.lastSweep = now;
      for (const buffer of breadcrumbBudget.buffers) if (now > buffer.expiresAt) buffer.clear();
    }
    while (this.entries.length >= this.maxCount || this.bytes + line.length > this.maxBytes) {
      const removed = this.entries.shift();
      if (!removed) break;
      this.bytes -= removed.length;
      breadcrumbBudget.bytes -= removed.length;
    }
    while (breadcrumbBudget.bytes + line.length > MAX_BREADCRUMB_BYTES || !breadcrumbBudget.buffers.has(this) && breadcrumbBudget.buffers.size >= MAX_BREADCRUMB_BUFFERS) {
      breadcrumbBudget.buffers.values().next().value?.clear();
      diagnostic("breadcrumbs_coalesced");
    }
    breadcrumbBudget.buffers.add(this);
    this.entries.push(line);
    this.bytes += line.length;
    breadcrumbBudget.bytes += line.length;
  }
  summary(maxBytes = 3072) {
    if (Date.now() > this.expiresAt) this.clear();
    let result = "";
    for (const line of this.entries.slice().reverse()) {
      if (result.length + line.length + 1 > maxBytes) break;
      result = line + "\n" + result;
    }
    return result.trim();
  }
  clear() {
    breadcrumbBudget.bytes -= this.bytes;
    breadcrumbBudget.buffers.delete(this);
    this.entries = [];
    this.bytes = 0;
    this.expiresAt = Date.now() + this.ttlMs;
  }
  static get budget() {
    return { bytes: breadcrumbBudget.bytes, buffers: breadcrumbBudget.buffers.size };
  }
  get size() {
    return this.entries.length;
  }
  get byteLength() {
    return this.bytes;
  }
};
var STATE = /* @__PURE__ */ Symbol.for("di.observability.runtime.v1");
var globalState = globalThis;
var runtimeState = globalState[STATE] ??= {
  diagnostics: {},
  diagnosticAt: 0,
  level: "info"
};
function diagnostic(reason) {
  runtimeState.diagnostics[reason] = (runtimeState.diagnostics[reason] ?? 0) + 1;
  const now = Date.now();
  if (now - runtimeState.diagnosticAt < 6e4) return;
  runtimeState.diagnosticAt = now;
  try {
    runtimeState.diagnosticSink?.(reason, runtimeState.diagnostics[reason]);
  } catch {
  }
}

// src/uuid.ts
function randomUUID() {
  const { crypto } = globalThis;
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = bytes[6] & 15 | 64;
  bytes[8] = bytes[8] & 63 | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// src/logger.ts
function createLogger(scope, provider, getOperation) {
  const logger = (provider ?? logs).getLogger(scope, SCHEMA_VERSION);
  function write(event, input, error, hasError = false) {
    try {
      const operation = getOperation?.() ?? currentOperation();
      const { attributes, invalid } = sanitizeAttributes(event.attributes, input);
      if (invalid) {
        diagnostic("contract_invalid");
        attributes["di.contract_violation"] = true;
      }
      const exception = hasError ? safeException(error) : void 0;
      const errorObject = error && (typeof error === "object" || typeof error === "function") ? error : void 0;
      const originalErrorId = errorObject ? operation?.errorIds?.get(errorObject) : void 0;
      const outcome = event.outcome ?? (event.level === "error" || event.level === "fatal" ? "failure" : event.level === "warn" ? "degraded" : "milestone");
      if (outcome !== "milestone") applyOutcome(operation, outcome);
      if (outcome === "failure")
        trace.getSpan(context.active())?.setStatus({ code: SpanStatusCode.ERROR });
      if (outcome === "failure" && (event.level === "error" || event.level === "fatal"))
        runtimeState.onFailureRecord?.(event.name, exception);
      if (LEVELS[event.level] < LEVELS[runtimeState.level]) return;
      if (!operation?.policy?.eligible(
        event.level,
        String(attributes["di.reason_code"] ?? "none"),
        String(exception?.["exception.type"] ?? "none")
      ) && operation?.policy)
        return;
      if (event.volume !== "aggregate" && operation?.policy?.policy !== "aggregate" && !runtimeState.repetition?.eligible(
        event.name,
        event.level,
        String(attributes["di.reason_code"] ?? "none"),
        String(exception?.["exception.type"] ?? "none")
      ) && runtimeState.repetition)
        return;
      const eventId = randomUUID();
      Object.assign(attributes, operation?.identifiers, {
        "di.log_schema_version": SCHEMA_VERSION,
        "di.policy_version": POLICY_VERSION,
        "di.event_id": eventId
      });
      if (hasError) {
        if (!originalErrorId) Object.assign(attributes, exception);
        attributes["di.error_id"] = originalErrorId ?? eventId;
        if (errorObject && operation && !originalErrorId)
          (operation.errorIds ??= /* @__PURE__ */ new WeakMap()).set(errorObject, eventId);
        let history = operation;
        while (history?.parent) history = history.parent;
        const summary = history?.breadcrumbs.summary();
        if (summary) attributes["di.breadcrumbs"] = summary;
        if (operation) {
          if (outcome !== "milestone") applyOutcome(operation, outcome, originalErrorId ?? eventId);
        }
      }
      const now = Date.now();
      logger.emit({
        eventName: event.name,
        body: event.message,
        severityNumber: LEVELS[event.level],
        severityText: event.level.toUpperCase(),
        timestamp: now,
        observedTimestamp: now,
        context: context.active(),
        attributes
      });
      return eventId;
    } catch {
      diagnostic("serialization_failed");
      return void 0;
    }
  }
  return {
    emit(event, attributes) {
      return write(event, attributes);
    },
    error(event, error, attributes) {
      return write(event, attributes, error, true);
    }
  };
}

// src/encoding.ts
var encoder = new TextEncoder();
var serializedBytes = (value) => encoder.encode(value).byteLength;
function timestampNanoseconds(time) {
  const seconds = time[0];
  const fraction = time[1];
  if (!Number.isSafeInteger(seconds) || seconds < 0 || !Number.isInteger(fraction) || fraction < 0 || fraction >= 1e9)
    return "0";
  return `${seconds}${String(fraction).padStart(9, "0")}`.replace(/^0+(?=\d)/, "");
}
function recordView(record) {
  const span = record.spanContext;
  return {
    schema_version: SCHEMA_VERSION,
    timestamp: timestampNanoseconds(record.hrTime),
    observed_timestamp: timestampNanoseconds(record.hrTimeObserved),
    event_name: record.eventName ?? "telemetry.invalid",
    body: typeof record.body === "string" ? record.body : "Telemetry record",
    severity_number: record.severityNumber ?? 17,
    severity_text: record.severityText ?? "ERROR",
    resource: { ...record.resource.attributes },
    scope: { name: record.instrumentationScope.name, version: record.instrumentationScope.version },
    attributes: { ...record.attributes },
    ...span ? { trace_id: span.traceId, span_id: span.spanId, trace_flags: span.traceFlags } : {}
  };
}
function encodeRecord(record) {
  const view = recordView(record);
  return JSON.stringify(view) + "\n";
}
var SafeguardProcessor = class {
  constructor(children) {
    this.children = children;
  }
  onEmit(record) {
    try {
      if (record.attributes["di.log_schema_version"] !== SCHEMA_VERSION || !record.eventName || !record.attributes["di.event_id"]) {
        diagnostic("unapproved_record");
        return;
      }
      let size = serializedBytes(encodeRecord(record));
      if (size > 16 * 1024) {
        record.setAttribute("di.truncated", true);
        record.setAttribute(
          "exception.stacktrace",
          String(record.attributes["exception.stacktrace"] ?? "").slice(0, 1024)
        );
        record.setAttribute(
          "di.breadcrumbs",
          String(record.attributes["di.breadcrumbs"] ?? "").slice(0, 512)
        );
        size = serializedBytes(encodeRecord(record));
      }
      if (size > 16 * 1024) {
        diagnostic("record_oversize");
        return;
      }
      for (const child of this.children) child.onEmit(record);
    } catch {
      diagnostic("serialization_failed");
    }
  }
  async forceFlush() {
    await Promise.allSettled(this.children.map((child) => child.forceFlush()));
  }
  async shutdown() {
    await Promise.allSettled(this.children.map((child) => child.shutdown()));
  }
};

// src/browserRequestFailure.ts
var NETWORK_MESSAGE = /^(?:Failed to fetch( \(.*\))?|network error|Load failed|The (network connection was lost|Internet connection appears to be offline|request timed out)\.?|NetworkError when attempting to fetch resource\.?|Error in input stream)$/i;
var NETWORK_CODES = /* @__PURE__ */ new Set(["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"]);
function isCancelledRequest(error) {
  const type = exceptionType(error);
  return type === "AbortError" || type === "CanceledError" || own(error, "code") === "ERR_CANCELED";
}
function isConnectivityFailure(error) {
  if (exceptionStatus(error) !== void 0) return false;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  const type = exceptionType(error);
  const code = own(error, "code");
  const message = own(error, "message");
  return type === "TimeoutError" || typeof code === "string" && NETWORK_CODES.has(code) || type === "TypeError" && typeof message === "string" && NETWORK_MESSAGE.test(message);
}
function classifyRequestFailure(error, status = exceptionStatus(error)) {
  if (isCancelledRequest(error)) return "cancelled";
  if (status !== void 0) return status >= 500 ? "server" : "failure";
  return isConnectivityFailure(error) ? "unavailable" : "failure";
}

// src/browserRuntime.ts
var runtimeFailed = defineEvent({
  name: "browser.runtime.failed",
  message: "Browser runtime operation failed",
  level: "error",
  classification: "operational",
  volume: "boundary",
  owner: "browser/runtime",
  attributes: {
    "di.reason_code": {
      type: "category",
      values: ["bootstrap", "render", "unhandled_error", "unhandled_rejection", "resource_load"]
    }
  }
});
var runtimeRecovered = defineEvent({
  name: "browser.runtime.recovered",
  message: "Browser rendering recovered from an error",
  level: "warn",
  classification: "operational",
  volume: "boundary",
  owner: "browser/runtime",
  attributes: {}
});
var runtimeDegraded = /* @__PURE__ */ defineEvent({
  name: "browser.runtime.degraded",
  message: "Browser runtime error not attributable to the application",
  level: "warn",
  classification: "operational",
  volume: "boundary",
  owner: "browser/runtime",
  attributes: {
    "di.reason_code": {
      type: "category",
      values: ["unattributed_error", "unattributed_rejection", "connectivity_rejection"]
    }
  }
});
var reported = /* @__PURE__ */ new WeakMap();
var installed = /* @__PURE__ */ new WeakMap();
function firstReport(observability, error) {
  if (error === null || typeof error !== "object" && typeof error !== "function") return true;
  const errors = reported.get(observability) ?? /* @__PURE__ */ new WeakSet();
  reported.set(observability, errors);
  if (errors.has(error)) return false;
  errors.add(error);
  return true;
}
function reportBrowserRuntimeError(observability, error, reason, recovered = false) {
  if (!firstReport(observability, error)) return;
  observability.addBreadcrumb("browser.runtime.boundary", { reason, recovered });
  const logger = observability.logger("browser.runtime");
  if (recovered) logger.error(runtimeRecovered, error, {});
  else logger.error(runtimeFailed, error, { "di.reason_code": reason });
}
function foreignScript(source, origin) {
  try {
    return new URL(source, origin).origin !== origin;
  } catch {
    return true;
  }
}
var FRAME_SOURCE = /([a-z][a-z0-9+.-]*:\/\/[^\s()@]+?|user-script:\d+):\d+:\d+/i;
function throwingScript(error) {
  const stack = errorStack(error);
  if (typeof stack !== "string") return void 0;
  const lines = stack.split("\n", 12);
  const frames = lines.some((line) => /^\s+at\s/.test(line)) ? lines.filter((line) => /^\s+at\s/.test(line)) : lines;
  for (const line of frames) {
    const source = FRAME_SOURCE.exec(line)?.[1];
    if (source) return source;
  }
  return void 0;
}
var INJECTED_BRIDGE = /\bWKWebView\b|webkit\.messageHandlers|runtime\.sendMessage/;
function thrower(error, origin) {
  const source = throwingScript(error);
  if (source) return foreignScript(source, origin) ? "foreign" : "application";
  const message = own(error, "message");
  return typeof message === "string" && INJECTED_BRIDGE.test(message) ? "foreign" : "unattributed";
}
function loadsApplicationCode(element, origin) {
  const source = element?.localName === "script" ? element.src : element?.localName === "link" ? element.href : void 0;
  return typeof source === "string" && !!source && !foreignScript(source, origin);
}
var isLoadHint = (element) => element?.localName === "link" && typeof element.rel === "string" && /\b(?:modulepreload|preload|prefetch)\b/i.test(element.rel);
function installBrowserRuntimeEvents(observability, target = window) {
  const existing = installed.get(observability);
  if (existing) return existing;
  const origin = () => target.location.origin;
  const degrade = (error, reason) => {
    if (firstReport(observability, error))
      observability.logger("browser.runtime").error(runtimeDegraded, error, { "di.reason_code": reason });
  };
  const report = (error, reason) => {
    const source = thrower(error, origin());
    if (source === "application") reportBrowserRuntimeError(observability, error, reason);
    else if (source === "unattributed")
      degrade(
        error,
        reason === "unhandled_error" ? "unattributed_error" : "unattributed_rejection"
      );
  };
  const onError = (event) => {
    if (!("error" in event)) {
      const element = event.target;
      if (!loadsApplicationCode(element, origin())) return;
      if (isLoadHint(element))
        observability.addBreadcrumb("browser.resource.failed", { reason: "load_hint" });
      else reportBrowserRuntimeError(observability, void 0, "resource_load");
      return;
    }
    const filename = "filename" in event && typeof event.filename === "string" ? event.filename : "";
    if (filename && foreignScript(filename, origin())) return;
    const error = event.error;
    if (error === null || typeof error !== "object") {
      observability.addBreadcrumb("browser.runtime.notice", { reason: "no_error_object" });
      return;
    }
    report(error, "unhandled_error");
  };
  const onRejection = (event) => {
    const failure = classifyRequestFailure(event.reason);
    if (failure === "cancelled") return;
    if (failure !== "unavailable") report(event.reason, "unhandled_rejection");
    else if (thrower(event.reason, origin()) !== "foreign")
      degrade(event.reason, "connectivity_rejection");
  };
  target.addEventListener("error", onError, { capture: true });
  target.addEventListener("unhandledrejection", onRejection);
  const dispose = () => {
    target.removeEventListener("error", onError, { capture: true });
    target.removeEventListener("unhandledrejection", onRejection);
    installed.delete(observability);
  };
  installed.set(observability, dispose);
  return dispose;
}

// src/browser.ts
var REPEAT_WINDOW_MS = 5 * 6e4;
var REPEATS_PER_GROUP = 3;
var MIN_BACKOFF_MS = 5e3;
var MAX_BACKOFF_MS = 5 * 6e4;
var BrowserReportExporter = class {
  constructor(options) {
    this.options = options;
  }
  queue = [];
  bytes = 0;
  sending = false;
  stopped = false;
  seen = /* @__PURE__ */ new Map();
  groups = /* @__PURE__ */ new Map();
  backoffMs = 0;
  pausedUntil = 0;
  /** A polling failure repeats the same event every tick; keep the first few per window. */
  repeated(record, now) {
    const key = [
      record.event_name,
      record.attributes["di.reason_code"],
      record.attributes["exception.type"]
    ].join("|");
    for (const [group, entry2] of this.groups)
      if (now - entry2.since > REPEAT_WINDOW_MS) this.groups.delete(group);
    const entry = this.groups.get(key);
    if (entry) return ++entry.count > REPEATS_PER_GROUP;
    if (this.groups.size >= 64) this.groups.delete(this.groups.keys().next().value);
    this.groups.set(key, { since: now, count: 1 });
    return false;
  }
  export(records) {
    if (!this.options.report || this.stopped) return;
    for (const source of records) {
      if ((source.severityNumber ?? 0) < 13) continue;
      const record = recordView(source);
      const id = String(record.attributes["di.event_id"]);
      const now = Date.now();
      for (const [key, at] of this.seen) if (now - at > 6e4) this.seen.delete(key);
      if (this.seen.has(id)) continue;
      if (this.seen.size >= 64) this.seen.delete(this.seen.keys().next().value);
      this.seen.set(id, now);
      if (now < this.pausedUntil || this.repeated(record, now)) {
        diagnostic("browser_dropped");
        continue;
      }
      const bytes = serializedBytes(JSON.stringify(record));
      if (this.queue.length >= Math.min(this.options.maxPending ?? 32, 32) || this.bytes + bytes > 256 * 1024) {
        diagnostic("browser_dropped");
        continue;
      }
      this.queue.push({ record, created: now, bytes });
      this.bytes += bytes;
    }
    void this.pump();
  }
  async pump() {
    if (this.sending || this.stopped) return;
    this.sending = true;
    try {
      while (this.queue.length && !this.stopped) {
        const item = this.queue.shift();
        this.bytes -= item.bytes;
        if (Date.now() - item.created > 6e4) continue;
        let timer;
        try {
          await Promise.race([
            this.options.report(item.record),
            new Promise((_, reject) => {
              timer = setTimeout(() => reject(new Error("Report timeout")), 5e3);
            })
          ]);
          this.backoffMs = 0;
        } catch {
          diagnostic("browser_report_failed");
          this.backoffMs = Math.min(
            this.backoffMs ? this.backoffMs * 2 : MIN_BACKOFF_MS,
            MAX_BACKOFF_MS
          );
          this.pausedUntil = Date.now() + this.backoffMs;
          for (let n = 0; n < this.queue.length; n++) diagnostic("browser_dropped");
          this.queue = [];
          this.bytes = 0;
        } finally {
          clearTimeout(timer);
        }
      }
    } finally {
      this.sending = false;
    }
  }
  async shutdown() {
    this.stopped = true;
    this.queue = [];
    this.bytes = 0;
  }
  get pending() {
    return { records: this.queue.length, bytes: this.bytes };
  }
};
var BROWSER = /* @__PURE__ */ Symbol.for("di.observability.browser.v1");
var g = globalThis;
function createBrowserObservability(options) {
  const instances = g[BROWSER] ??= /* @__PURE__ */ new Map();
  const existing = instances.get(options.serviceName);
  if (existing) return existing;
  const resource = {
    attributes: {
      "service.name": safeCategory(options.serviceName) ?? "browser",
      "service.version": safeCategory(options.serviceVersion) ?? "unknown",
      "service.instance.id": randomUUID(),
      "deployment.environment.name": safeCategory(options.environment) ?? "unknown"
    }
  };
  const exporter = new BrowserReportExporter(options);
  const exportProcessor = {
    onEmit: (record) => {
      try {
        exporter.export([record]);
      } catch {
      }
    },
    forceFlush: async () => {
    },
    shutdown: () => exporter.shutdown()
  };
  const provider = new BrowserLoggerProvider({
    resource,
    limits: { attributeCountLimit: 64, attributeValueLengthLimit: 8192 },
    processors: [new SafeguardProcessor([exportProcessor])]
  });
  const state = {
    identifiers: {},
    errorIds: /* @__PURE__ */ new WeakMap(),
    breadcrumbs: new BreadcrumbBuffer(50, 32 * 1024)
  };
  const result = {
    logger: (scope) => createLogger(scope, provider, () => state),
    addBreadcrumb: (name, fields) => state.breadcrumbs.add(name, fields),
    clearBreadcrumbs: () => {
      state.breadcrumbs.clear();
      state.identifiers = {};
      delete state.errorId;
      state.errorIds = /* @__PURE__ */ new WeakMap();
      delete state.outcome;
    },
    setContext: (ids) => {
      for (const [key, value] of Object.entries(ids)) {
        const id = safeIdentifier(value);
        if (id) state.identifiers[key] = id;
      }
    },
    shutdown: async () => {
      state.breadcrumbs.clear();
      await provider.shutdown();
      instances.delete(options.serviceName);
    },
    breadcrumbs: state.breadcrumbs,
    pending: () => exporter.pending
  };
  instances.set(options.serviceName, result);
  return result;
}
export {
  BreadcrumbBuffer,
  classifyRequestFailure,
  createBrowserObservability,
  defineEvent,
  installBrowserRuntimeEvents,
  isCancelledRequest,
  isConnectivityFailure,
  randomUUID,
  reportBrowserRuntimeError,
  safeException
};
